import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { usePrayer } from '../../contexts/PrayerContext';
import { MosqueIcon, CrescentStarIcon } from '../common/IslamicIcons';

/**
 * Ramadan & Special Times — display-only card
 * Shows Imsak, Suhoor, Iftar (Maghrib), Sunrise, Midnight, Tahajjud
 */
const RamadanTimings = () => {
  const { t } = useTranslation();
  const { isDarkMode, language, isRTL, formatTime } = useTheme();
  const { prayerTimes, extraTimings, loading } = usePrayer();

  if (loading || !prayerTimes || !extraTimings) return null;

  // Tahajjud = Lastthird of night
  const timings = [
    {
      key: 'nusuk',
      label: t('ramadan.nusuk'),
      desc: t('ramadan.nusukDesc'),
      time: extraTimings.Imsak,        // Imsak = start of Nusuk worship / ritual time
      icon: <CrescentStarIcon size={16} color="currentColor" />,
      accent: isDarkMode ? 'text-violet-400' : 'text-violet-600',
      bg: isDarkMode ? 'bg-violet-500/10 border-violet-500/20' : 'bg-violet-50 border-violet-200',
    },
    {
      key: 'suhoor',
      label: t('ramadan.suhoor'),
      desc: t('ramadan.suhoorDesc'),
      time: extraTimings.Imsak,
      icon: <MosqueIcon size={16} color="currentColor" />,
      accent: isDarkMode ? 'text-indigo-400' : 'text-indigo-600',
      bg: isDarkMode ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200',
    },
    {
      key: 'iftar',
      label: t('ramadan.iftar'),
      desc: t('ramadan.iftarDesc'),
      time: prayerTimes.Maghrib,
      icon: <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm0 14a6 6 0 110-12 6 6 0 010 12z" opacity="0.3"/><path d="M10 6a4 4 0 100 8 4 4 0 000-8zm0 6a2 2 0 110-4 2 2 0 010 4z"/></svg>,
      accent: isDarkMode ? 'text-orange-400' : 'text-orange-600',
      bg: isDarkMode ? 'bg-orange-500/10 border-orange-500/20' : 'bg-orange-50 border-orange-200',
    },
    {
      key: 'sunrise',
      label: t('ramadan.sunrise'),
      desc: t('ramadan.sunriseDesc'),
      time: extraTimings.Sunrise,
      icon: <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd"/></svg>,
      accent: isDarkMode ? 'text-amber-400' : 'text-amber-600',
      bg: isDarkMode ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200',
    },
    {
      key: 'midnight',
      label: t('ramadan.midnight'),
      desc: t('ramadan.midnightDesc'),
      time: extraTimings.Midnight,
      icon: <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z"/></svg>,
      accent: isDarkMode ? 'text-slate-400' : 'text-slate-600',
      bg: isDarkMode ? 'bg-slate-500/10 border-slate-500/20' : 'bg-slate-50 border-slate-200',
    },
    {
      key: 'tahajjud',
      label: t('ramadan.tahajjud'),
      desc: t('ramadan.tahajjudDesc'),
      time: extraTimings.Lastthird,
      icon: <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>,
      accent: isDarkMode ? 'text-rose-400' : 'text-rose-600',
      bg: isDarkMode ? 'bg-rose-500/10 border-rose-500/20' : 'bg-rose-50 border-rose-200',
    },
  ];

  return (
    <div
      data-testid="ramadan-timings"
      className={`rounded-3xl overflow-hidden ${
        isDarkMode
          ? 'bg-gradient-to-br from-[var(--card-dark-from)] to-[var(--card-dark-to)] border border-white/8'
          : 'bg-white shadow-xl border border-gray-100'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className={`px-5 pt-4 pb-3 flex items-center gap-2 border-b ${
        isDarkMode ? 'border-white/8' : 'border-gray-100'
      } ${isRTL ? 'flex-row-reverse' : ''}`}>
        <MosqueIcon size={20} color={isDarkMode ? 'rgba(251,191,36,0.7)' : 'var(--p-hex)'} />
        <div>
          <h3
            className={`text-sm font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-[var(--p-hex)]'}`}
            
          >
            {t('ramadan.title')}
          </h3>
        </div>
        <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${
          isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'
        }`}>
          {language === 'ar' ? 'للعرض فقط' : 'Display only'}
        </span>
      </div>

      {/* Grid of timings */}
      <div className="p-4 grid grid-cols-2 gap-2.5">
        {timings.map(item => (
          <div
            key={item.key}
            className={`flex items-center gap-3 p-3 rounded-2xl border ${item.bg} ${isRTL ? 'flex-row-reverse' : ''}`}
          >
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${item.accent} ${
              isDarkMode ? 'bg-white/8' : 'bg-white shadow-sm'
            }`}>
              {item.icon}
            </div>
            <div className={`min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
              <p className={`text-xs font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                {item.label}
              </p>
              <p className={`text-sm font-semibold tabular-nums ${item.accent}`}>
                {item.time ? formatTime(item.time) : '—'}
              </p>
              <p className={`text-[10px] truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                {item.desc}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RamadanTimings;
